import { ChevronDown, Inbox, type LucideIcon } from "lucide-react";
import * as React from "react";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/v2/constants/testIds";
import { useIsMobile, useIsTablet } from "@/v2/hooks/useMediaQuery";
import {
  ActionMenu,
  type ActionMenuConfig,
} from "@/v2/ui/composed/action-menu";
import { DataTablePagination } from "@/v2/ui/composed/data-table/DataTablePagination";
import { Loading } from "@/v2/ui/composed/loading";
import { Card } from "@/v2/ui/primitives/card";
import { displayValue } from "@/v2/ui/primitives/display-value";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/v2/ui/primitives/table";

/**
 * Column definition for the table view
 */
export interface ResponsiveDataViewColumn<T> {
  /** Unique identifier for the column */
  id: string;
  /** Column header text */
  header: string;
  /** Property accessor - can be a key of T or a dot-notation path */
  accessor?: keyof T | string;
  /** Custom cell renderer */
  cell?: (row: T) => React.ReactNode;
  /** Column width (CSS value) */
  width?: number | string;
  /** Whether column is hidden on certain breakpoints */
  hideOnMobile?: boolean;
  /** Whether to stop click propagation on this cell (useful for inline Select/Link) */
  stopPropagation?: boolean;
}

/**
 * Pagination configuration for ResponsiveDataView
 */
export interface ResponsiveDataViewPagination {
  /** Current page (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Current page size */
  pageSize: number;
  /** Callback when page size changes */
  onPageSizeChange: (size: number) => void;
  /** Total number of items */
  totalItems: number;
}

/**
 * Props for the ResponsiveDataView component
 */
export interface ResponsiveDataViewProps<T> {
  /** Array of data items to display */
  data: T[];
  /** Column definitions for table view */
  columns: ResponsiveDataViewColumn<T>[];
  /** Render function for mobile card view. Receives isExpanded state when expandedRows is provided. */
  renderCard: (item: T, index: number, isExpanded?: boolean) => React.ReactNode;
  /** Function to extract unique key from each item */
  keyExtractor: (item: T) => string | number;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Message to show when no data is available */
  emptyMessage?: string;
  /** Description text below empty message */
  emptyDescription?: string;
  /** Icon to show in empty state */
  emptyIcon?: LucideIcon;
  /** Pagination configuration (optional) */
  pagination?: ResponsiveDataViewPagination;
  /** Callback when a row is clicked (table view) */
  onRowClick?: (item: T) => void;
  /** Additional className for the container */
  className?: string;
  /** Additional className for the table card wrapper */
  tableClassName?: string;
  /** Additional className for the mobile card list wrapper */
  mobileClassName?: string;
  /** Minimum height for the table container */
  minHeight?: string;
  /** Whether to show table header */
  showHeader?: boolean;
  /** Set of expanded row IDs (use with useExpandableRows hook) */
  expandedRows?: Set<string | number>;
  /** Callback when a row's expansion is toggled */
  onRowToggle?: (id: string | number) => void;
  /** Render function for expanded row content in table view */
  renderExpandedContent?: (item: T) => React.ReactNode;
  /** Action menu configuration - adds an action column with dropdown menu */
  actionMenu?: ActionMenuConfig<T>;
  /** Custom className for expanded row content (default: tw-bg-muted/20 hover:tw-bg-muted/20) */
  expandedRowClassName?: string;
}

/**
 * Get nested value from object using dot notation
 */
function getNestedValue<T>(obj: T, path: string | keyof T): unknown {
  if (typeof path !== "string") {
    return obj[path];
  }

  const keys = path.split(".");
  let result: unknown = obj;

  for (const key of keys) {
    if (result === null || result === undefined) {
      return undefined;
    }
    result = (result as Record<string, unknown>)[key];
  }

  return result;
}

/**
 * Responsive table/card view component
 *
 * Renders data as a table on desktop (md breakpoint and above)
 * and as a card list on mobile (below md breakpoint).
 *
 * Supports expandable rows via the `expandedRows`, `onRowToggle`, and
 * `renderExpandedContent` props. Use with `useExpandableRows` hook.
 *
 * @example
 * ```tsx
 * const expandable = useExpandableRows()
 *
 * <ResponsiveDataView
 *   data={bikes}
 *   columns={[
 *     { id: 'name', header: 'Name', accessor: 'name' },
 *     { id: 'status', header: 'Status', cell: (row) => <StatusBadge status={row.status} /> },
 *   ]}
 *   renderCard={(bike, index, isExpanded) => <BikeCard bike={bike} isExpanded={isExpanded} />}
 *   keyExtractor={(bike) => bike.id}
 *   isLoading={isLoading}
 *   expandedRows={expandable.expandedRows}
 *   onRowToggle={expandable.toggleRowExpanded}
 *   renderExpandedContent={(bike) => <BikeDetails bike={bike} />}
 *   pagination={{...}}
 * />
 * ```
 */
export function ResponsiveDataView<T>({
  data,
  columns,
  renderCard,
  keyExtractor,
  isLoading = false,
  emptyMessage = strings("common.noDataAvailable"),
  emptyDescription = strings("common.tryAdjustingFilters"),
  emptyIcon: EmptyIcon = Inbox,
  pagination,
  onRowClick,
  className,
  tableClassName,
  mobileClassName,
  minHeight = "400px",
  showHeader = true,
  expandedRows,
  onRowToggle,
  renderExpandedContent,
  actionMenu,
  expandedRowClassName = "tw-bg-muted/20 hover:tw-bg-muted/20",
}: ResponsiveDataViewProps<T>) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const safeData = Array.isArray(data) ? data : [];
  const isEmpty = !isLoading && safeData.length === 0;

  // Filter out columns marked as hideOnMobile when on tablet viewport
  // (tablet is the smallest viewport where table view is shown)
  const visibleColumns = isTablet
    ? columns.filter((column) => !column.hideOnMobile)
    : columns;

  // Mobile card view
  if (isMobile) {
    return (
      <div
        className={cn("tw-flex tw-flex-col tw-flex-1 tw-min-h-0", className)}
      >
        <div
          className={cn(
            "tw-relative tw-flex-1 tw-overflow-auto",
            mobileClassName
          )}
        >
          {isLoading && (
            <div className="tw-absolute tw-inset-0 tw-bg-background/80 tw-z-[15] tw-flex tw-items-center tw-justify-center tw-rounded-lg">
              <Loading size="md" />
            </div>
          )}

          {isEmpty && (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-text-center tw-py-12">
              <EmptyIcon className="tw-h-10 tw-w-10 tw-text-muted-foreground/50" />
              <p className="tw-text-muted-foreground">{emptyMessage}</p>
              {emptyDescription && (
                <p className="tw-text-sm tw-text-muted-foreground/70">
                  {emptyDescription}
                </p>
              )}
            </div>
          )}

          {!isEmpty && (
            <div className="tw-space-y-2 tw-pb-3">
              {safeData.map((item, index) => {
                const key = keyExtractor(item);
                const isExpanded = expandedRows?.has(key);
                return (
                  <React.Fragment key={key}>
                    {renderCard(item, index, isExpanded)}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {pagination && (
          <div className="tw-flex-shrink-0 tw-pt-4">
            <DataTablePagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={pagination.onPageChange}
              pageSize={pagination.pageSize}
              onPageSizeChange={pagination.onPageSizeChange}
              totalItems={pagination.totalItems}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
    );
  }

  // Desktop table view
  return (
    <div className={cn("tw-flex tw-flex-col tw-flex-1 tw-min-h-0", className)}>
      <Card
        className={cn(
          "tw-w-full tw-flex-1 tw-flex tw-flex-col tw-overflow-hidden tw-border tw-relative",
          tableClassName
        )}
        style={{ minHeight }}
      >
        {isLoading && (
          <div className="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-background/85 tw-z-[15] tw-flex tw-items-center tw-justify-center">
            <Loading size="md" />
          </div>
        )}

        {isEmpty && (
          <div className="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-background/85 tw-z-[15] tw-flex tw-items-center tw-justify-center">
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-text-center">
              <EmptyIcon className="tw-h-10 tw-w-10 tw-text-muted-foreground/50" />
              <p className="tw-text-muted-foreground">{emptyMessage}</p>
              <p className="tw-text-sm tw-text-muted-foreground/70">
                {emptyDescription}
              </p>
            </div>
          </div>
        )}

        <div className="tw-flex-1 tw-overflow-auto">
          <Table data-testid={TEST_IDS.dataTable}>
            {showHeader && (
              <TableHeader className="tw-sticky tw-top-0 tw-bg-background tw-z-10">
                <TableRow>
                  {renderExpandedContent && <TableHead style={{ width: 40 }} />}
                  {visibleColumns.map((column) => (
                    <TableHead
                      key={column.id}
                      style={{
                        whiteSpace: "nowrap",
                        ...(column.width ? { width: column.width } : {}),
                      }}
                    >
                      {column.header}
                    </TableHead>
                  ))}
                  {actionMenu && <TableHead style={{ width: 60 }} />}
                </TableRow>
              </TableHeader>
            )}
            <TableBody>
              {safeData.map((row) => {
                const rowKey = keyExtractor(row);
                const isExpanded = expandedRows?.has(rowKey) ?? false;
                return (
                  <React.Fragment key={rowKey}>
                    <TableRow
                      onClick={() => onRowClick?.(row)}
                      className={cn(
                        onRowClick && "tw-cursor-pointer hover:tw-bg-muted/50",
                        isExpanded && "tw-bg-muted/30"
                      )}
                    >
                      {renderExpandedContent && (
                        <TableCell className="tw-w-10 tw-px-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRowToggle?.(rowKey);
                            }}
                            className="tw-p-1 tw-rounded hover:tw-bg-muted tw-transition-colors"
                          >
                            <ChevronDown
                              className={cn(
                                "tw-h-4 tw-w-4 tw-text-muted-foreground tw-transition-transform",
                                isExpanded && "tw-rotate-180"
                              )}
                            />
                          </button>
                        </TableCell>
                      )}
                      {visibleColumns.map((column) => {
                        const cellContent = column.cell
                          ? column.cell(row)
                          : column.accessor
                            ? displayValue(getNestedValue(row, column.accessor))
                            : null;

                        return (
                          <TableCell
                            key={column.id}
                            onClick={
                              column.stopPropagation
                                ? (e) => e.stopPropagation()
                                : undefined
                            }
                          >
                            {cellContent}
                          </TableCell>
                        );
                      })}
                      {actionMenu && (
                        <TableCell
                          className="tw-text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ActionMenu row={row} config={actionMenu} />
                        </TableCell>
                      )}
                    </TableRow>
                    {isExpanded && renderExpandedContent && (
                      <TableRow className={expandedRowClassName}>
                        <TableCell
                          colSpan={
                            visibleColumns.length + 1 + (actionMenu ? 1 : 0)
                          }
                          className="tw-p-0"
                        >
                          <div className="tw-px-4 tw-py-3">
                            {renderExpandedContent(row)}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      {pagination && (
        <div className="tw-flex-shrink-0 tw-pt-4">
          <DataTablePagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={pagination.onPageChange}
            pageSize={pagination.pageSize}
            onPageSizeChange={pagination.onPageSizeChange}
            totalItems={pagination.totalItems}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
}

ResponsiveDataView.displayName = "ResponsiveDataView";
